import {
  Component,
  ViewChild,
  Input,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ElementRef,
  HostListener,
  Output,
  EventEmitter,
  AfterContentInit,
} from '@angular/core';

import { inDemoMode } from '@lib/remote-login';

interface ISizeObject {
  width: number;
  height: number;
}

interface IImageSpatialDimensions {
  imageDimensions: ClientRect;
  imageContainerDimensions: ClientRect;
  imageWidth: number;
  imageHeight: number;
  containerWidth: number;
  containerHeight: number;
  originalImageWidth: number;
  originalImageHeight: number;
  originDiffW: number;
  originDiffH: number;
  originX: number;
  originY: number;
  spaceLeftEmpty: boolean;
  spaceRightEmpty: boolean;
  spaceTopEmpty: boolean;
  spaceBotEmpty: boolean;
}

export interface IImageData {
  imageData: string;
  base64Encoded: string[];
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'image-pan-zoom',
  styleUrls: ['./image-pan-zoom.component.scss'],
  templateUrl: './image-pan-zoom.component.html',
})
export class ImagePanZoomComponent implements AfterContentInit {
  @ViewChild('viewImageElement', { static: true }) public viewImageElement: ElementRef;
  @Input() public imageSrcUrl: string;
  @Input() public crossOrigin: string = '';
  @Input() public parentControlsImageVisibility: boolean = false;
  @Input() public fitToContainer: boolean = false;
  @Output() public onImageLoadComplete: EventEmitter<undefined> = new EventEmitter();
  public isImageVisible: boolean = false;
  private panEnabled: boolean = false;
  private imageElm: HTMLImageElement;
  private container: HTMLElement;
  private currentScale: number = 1;
  private matrixValues: number[] = [1, 0, 0, 1, 0, 0]; // [scaleX, skewX, skewY, scaleY, translateX, translateY]
  private positionsArray: number[][] = [];
  private animationTimeout: any;
  private imageDimensions: ClientRect;
  private imageContainerDimensions: ClientRect;
  private readonly ZOOM_VALUE: number = 0.6;
  private readonly minScale: number = 1;
  private readonly maxZoomValue: number = 200;
  private readonly zoomAnimationTime: number = 500;
  public readonly inDemoMode: boolean;

  constructor(private cdr: ChangeDetectorRef) {
    this.inDemoMode = inDemoMode();
  }

  public ngAfterContentInit() {
    if (this.crossOrigin) this.viewImageElement.nativeElement.crossOrigin = this.crossOrigin;
    this.imageElm = this.viewImageElement.nativeElement;
    this.container = this.imageElm.parentElement;
  }

  @HostListener('window:resize')
  public onImageLoad(): void {
    this.setImageVisibility(false);
    this.imageElm = this.viewImageElement.nativeElement;
    this.container = this.imageElm.parentElement;

    if (!this.parentControlsImageVisibility) {
      setTimeout(() => {
        this.resetAndRepositionImage();
        this.setImageVisibility(true);
      }, 300);
    }

    this.onImageLoadComplete.emit();
  }

  public resetAndRepositionImage(): void {
    this.resetZoom();
    this.resetImageTranform();
    this.fitImageToContainer();
    this.alignImageToContainer();
    this.recalculateImageAndContainerRects();
  }

  public setImageVisibility(isVisible: boolean): void {
    this.isImageVisible = isVisible;
    this.cdr.detectChanges();
  }

  public get percentageZoomText(): string {
    // Prevents displaying NaN% on initial load
    return Number.isFinite(this.imageZoomPercentage) ? `${this.imageZoomPercentage}%` : '';
  }

  public zoomIn(): void {
    if (this.imageZoomPercentage > this.maxZoomValue) return;

    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
      this.animationTimeout = null;
    }
    this.setZoomTransition();
    const matrix = this.matrixValues;
    let zoomX = matrix[0];
    let zoomY = matrix[3];

    this.currentScale = zoomY = zoomX += this.ZOOM_VALUE;

    if (this.imageZoomPercentage > this.maxZoomValue) this.currentScale = zoomY = zoomX = this.scaleAtMaxZoom;

    this.setMatrixValues([zoomX, matrix[1], matrix[2], zoomY, matrix[4], matrix[5]]);

    this.animationTimeout = setTimeout(() => {
      this.resetTransition();
      this.recalculateImageAndContainerRects();
    }, this.zoomAnimationTime);
  }

  public zoomOut(): void {
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
      this.animationTimeout = null;
    }
    this.setZoomTransition();
    let matrix = this.matrixValues;
    let zoomX = matrix[0];
    let zoomY = matrix[3];

    this.currentScale = zoomY = zoomX -= this.ZOOM_VALUE;

    if (zoomX < this.minScale) this.currentScale = zoomX = zoomY = this.minScale;

    matrix = this.zoomoutWidthReposition(matrix, this.currentScale);
    matrix = this.zoomoutHeightReposition(matrix, this.currentScale);

    this.setMatrixValues([zoomX, matrix[1], matrix[2], zoomY, matrix[4], matrix[5]]);

    this.animationTimeout = setTimeout(() => {
      this.resetTransition();
      this.recalculateImageAndContainerRects();
    }, this.zoomAnimationTime);
  }

  public rotateInDegrees(degrees: number): IImageData {
    const canvas = this.convertImageToCanvas(this.imageElm);
    const imageData: string = this.getRotatedImageData(degrees, canvas, this.imageElm);
    const base64Encoded: string[] = imageData.split(';base64,');

    return {
      imageData,
      base64Encoded,
    };
  }

  public resetZoom(): void {
    const matrix = this.matrixValues;
    this.currentScale = matrix[3] = matrix[0] = this.minScale;
    this.setMatrixValues(matrix);
  }

  public startPan(): void {
    this.panEnabled = true;
    this.setPanTransition();
    this.positionsArray = [];
  }

  @HostListener('document:mouseup')
  private stopPan(): void {
    this.panEnabled = false;
    this.resetTransition();
    this.positionsArray = [];
  }

  @HostListener('document:mousemove', ['$event'])
  private panImage(event: MouseEvent): void {
    if (!this.panEnabled) {
      this.positionsArray = [];
      return;
    }

    const currentPosition: number[] = [event.pageX, event.pageY];
    this.positionsArray.push(currentPosition);
    const positionsArray = this.positionsArray;
    const arrLength = this.positionsArray.length;
    if (positionsArray.length === 2) {
      this.moveImage(positionsArray);
      this.positionsArray = [];
    }
  }

  private zoomoutWidthReposition(matrix: number[], newScale: number): number[] {
    const imageContainerDimensions = this.imageContainerDimensions;
    const imageWidth = this.imageElm.clientWidth * newScale;
    const containerWidth = imageContainerDimensions.width;
    const originalImageWidth = this.imageElm.clientWidth;
    const originDiffW = (imageWidth - originalImageWidth) / 2;
    const originX = -(this.imageElm.offsetLeft) + originDiffW;
    let xPosition = matrix[4];
    const spaceLeftEmpty = xPosition > originX;
    const spaceRightEmpty = (xPosition + imageWidth) < containerWidth + originX;

    if (imageWidth >= containerWidth) {
      if (spaceLeftEmpty && !spaceRightEmpty) xPosition = originX;
      else if (!spaceLeftEmpty && spaceRightEmpty) xPosition = containerWidth - imageWidth + originX;
    } else if (imageWidth < containerWidth) {
      xPosition = (containerWidth - imageWidth) / 2;
      xPosition += originX;
    }

    matrix[4] = xPosition;

    return matrix;
  }

  private zoomoutHeightReposition(matrix: number[], newScale: number): number[] {
    const imageContainerDimensions = this.imageContainerDimensions;
    const imageHeight = this.imageElm.clientHeight * newScale;
    const contHeight = imageContainerDimensions.height;
    const originalImageHeight = this.imageElm.clientHeight;
    const originDiffH = (imageHeight - originalImageHeight) / 2;
    const originY = -(this.imageElm.offsetTop) + originDiffH;
    let yPosition = matrix[5];
    const spaceTopEmpty = yPosition > originY;
    const spaceBotEmpty = (yPosition + imageHeight) < contHeight + originY;

    if (imageHeight >= contHeight) {
      if (spaceTopEmpty && !spaceBotEmpty) yPosition = originY;
      else if (!spaceTopEmpty && spaceBotEmpty) yPosition = contHeight - imageHeight + originY;
    } else if (imageHeight < contHeight) {
      yPosition = (contHeight - imageHeight) / 2;
      yPosition += originY;
    }

    matrix[5] = yPosition;

    return matrix;
  }

  private moveImage(positionsArray: number[][]) {
    const horizontalDiff = positionsArray[1][0] - positionsArray[0][0];
    const verticalDiff = positionsArray[1][1] - positionsArray[0][1];
    const originalX = this.matrixValues[4];
    const originalY = this.matrixValues[5];
    let matrix = this.matrixValues;

    if (this.shouldImageMoveHorizontally(matrix)) matrix[4] += horizontalDiff;
    if (this.shouldImageMoveVertically(matrix)) matrix[5] += verticalDiff;

    matrix = this.containImageWithinContainerWidth(matrix, originalX);
    matrix = this.containImageWithinContainerHeight(matrix, originalY);

    this.setMatrixValues(matrix);
  }

  private getImageAndContainerSpatialDimensions(xPosition: number, yPosition: number): IImageSpatialDimensions {
    const imageDimensions: ClientRect = this.imageDimensions || this.imageElm.getBoundingClientRect();
    const imageContainerDimensions: ClientRect = this.imageContainerDimensions || this.container.getBoundingClientRect();
    const imageWidth = imageDimensions.width;
    const imageHeight = imageDimensions.height;
    const containerWidth = imageContainerDimensions.width;
    const containerHeight = imageContainerDimensions.height;
    const originalImageWidth = this.imageElm.clientWidth;
    const originalImageHeight = this.imageElm.clientHeight;
    const originDiffW = (imageWidth - originalImageWidth) / 2;
    const originDiffH = (imageHeight - originalImageHeight) / 2;
    const originX = -(this.imageElm.offsetLeft) + originDiffW;
    const originY = -(this.imageElm.offsetTop) + originDiffH;
    const spaceLeftEmpty = xPosition > originX;
    const spaceRightEmpty = (xPosition + imageWidth) < containerWidth + originX;
    const spaceTopEmpty = yPosition > originY;
    const spaceBotEmpty = (yPosition + imageHeight) < containerHeight + originY;

    return {
      imageDimensions,
      imageContainerDimensions,
      imageWidth,
      imageHeight,
      containerWidth,
      containerHeight,
      originalImageWidth,
      originalImageHeight,
      originDiffW,
      originDiffH,
      originX,
      originY,
      spaceLeftEmpty,
      spaceRightEmpty,
      spaceTopEmpty,
      spaceBotEmpty,
    };
  }

  private containImageWithinContainerWidth(currentMatrix: number[], originalX: number): number[] {
    const matrix = currentMatrix;
    let xPosition = matrix[4];
    const {
      imageWidth,
      containerWidth,
      originX,
      spaceLeftEmpty,
      spaceRightEmpty,
    } = this.getImageAndContainerSpatialDimensions(xPosition, matrix[5]);

    if (imageWidth >= containerWidth) {
      if (spaceLeftEmpty && !spaceRightEmpty) xPosition = originX;
      else if (!spaceLeftEmpty && spaceRightEmpty) xPosition = containerWidth - imageWidth + originX;
    } else if (imageWidth < containerWidth) {
      if (!spaceLeftEmpty && spaceRightEmpty) xPosition = originX;
      else if (spaceLeftEmpty && !spaceRightEmpty) xPosition = containerWidth - imageWidth + originX;
      else if (spaceLeftEmpty && spaceRightEmpty) xPosition = originalX;
    }

    matrix[4] = xPosition;

    return matrix;
  }

  private containImageWithinContainerHeight(currentMatrix: number[], originalY: number): number[] {
    const matrix = currentMatrix;
    let yPosition = matrix[5];
    const {
      imageHeight,
      containerHeight,
      originY,
      spaceTopEmpty,
      spaceBotEmpty,
    } = this.getImageAndContainerSpatialDimensions(matrix[4], yPosition);

    if (imageHeight >= containerHeight) {
      if (spaceTopEmpty && !spaceBotEmpty) yPosition = originY;
      else if (!spaceTopEmpty && spaceBotEmpty) yPosition = containerHeight - imageHeight + originY;
    } else if (imageHeight < containerHeight) {
      if (!spaceTopEmpty && spaceBotEmpty) yPosition = originY;
      else if (spaceTopEmpty && !spaceBotEmpty) yPosition = containerHeight - imageHeight + originY;
      else if (spaceTopEmpty && spaceBotEmpty) yPosition = originalY;
    }

    matrix[5] = yPosition;

    return matrix;
  }

  private shouldImageMoveHorizontally(matrix: number[]): boolean {
    const {
      spaceLeftEmpty,
      spaceRightEmpty,
    } = this.getImageAndContainerSpatialDimensions(matrix[4], matrix[5]);

    return !spaceLeftEmpty && !spaceRightEmpty;
  }

  private shouldImageMoveVertically(matrix: number[]): boolean {
    const {
      spaceTopEmpty,
      spaceBotEmpty,
    } = this.getImageAndContainerSpatialDimensions(matrix[4], matrix[5]);

    return !spaceTopEmpty && !spaceBotEmpty;
  }

  private setPanTransition(): void {
    if (!this.imageElm) return;
    this.imageElm.style.transition = 'none 0s ease 0s';
  }

  private resetTransition(): void {
    if (!this.imageElm) return;
    this.imageElm.style.transition = '';
  }

  private setZoomTransition(): void {
    if (!this.imageElm) return;
    this.imageElm.style.transition = `transform ${this.zoomAnimationTime}ms ease-in-out 0s`;
  }

  private resetImageTranform(): void {
    if (!this.imageElm) return;
    this.matrixValues = [1, 0, 0, 1, 0, 0];
    this.setMatrixValues(this.matrixValues);
  }

  private alignImageToContainer(): void {
    this.recalculateImageAndContainerRects();
    const matrix = this.matrixValues;
    const {
      imageWidth,
      imageHeight,
      containerWidth,
      containerHeight,
    } = this.getImageAndContainerSpatialDimensions(matrix[4], matrix[5]);

    const horizontalOffset = (containerWidth - imageWidth) / 2;
    const verticalOffset = (containerHeight - imageHeight) / 2;

    matrix[4] = horizontalOffset;
    matrix[5] = verticalOffset;

    this.setMatrixValues(matrix);
  }

  private fitImageToContainer(): void {
    const imageElm = this.imageElm;
    if (this.fitToContainer) this.fitImageToContainerWidthHeight();
    else if (imageElm.naturalHeight > imageElm.naturalWidth) this.fitImageHeightToContainer();
    else if (imageElm.naturalHeight < imageElm.naturalWidth) this.fitImageWidthToContainer();
    else this.fitImageToContainerWidthHeight();
  }

  private fitImageHeightToContainer(): void {
    const imageElm = this.imageElm;
    const container = this.container;
    const scaleToOffset: number = container.clientHeight / imageElm.naturalHeight;
    this.setImageWidthHeightToScale(scaleToOffset);
  }

  private fitImageWidthToContainer(): void {
    const imageElm = this.imageElm;
    const container = this.container;
    const scaleToOffset: number = container.clientWidth / imageElm.naturalWidth;
    this.setImageWidthHeightToScale(scaleToOffset);
  }

  private fitImageToContainerWidthHeight(): void {
    const imageElm = this.imageElm;
    const container = this.container;

    // Calculate scaling factors based on both width and height
    const widthScale = container.clientWidth / imageElm.naturalWidth;
    const heightScale = container.clientHeight / imageElm.naturalHeight;

    // Choose the minimum scaling factor to fit the image within the container while preserving aspect ratio
    const scale = Math.min(widthScale, heightScale);
    this.setImageWidthHeightToScale(scale);
  }

  private setImageWidthHeightToScale(scale: number): void {
    const imageElm = this.imageElm;
    imageElm.height = imageElm.naturalHeight * scale;
    imageElm.width = imageElm.naturalWidth * scale;
  }

  private convertImageToCanvas(image: HTMLImageElement): HTMLCanvasElement {
    const canvas: HTMLCanvasElement = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    canvas.getContext('2d').drawImage(image, 0, 0);
    return canvas;
  }

  private getRotatedImageData(degrees: number, canvas: HTMLCanvasElement, img: HTMLImageElement): string {
    const imgWidth = img.naturalWidth;
    const imgHeight = img.naturalHeight;
    const size = this.newCanvasSize(imgWidth, imgHeight, degrees);
    canvas.width = size.width;
    canvas.height = size.height;
    const context = canvas.getContext('2d');
    context.clearRect(0, 0, canvas.width, canvas.height);

    // save the unrotated context of the canvas so we can restore it later
    // the alternative is to untranslate & unrotate after drawing
    context.save();

    // move to the center of the canvas
    context.translate(canvas.width / 2, canvas.height / 2);

    // rotate the canvas to the specified degrees
    context.rotate(degrees * Math.PI / 180);

    // draw the image
    // since the context is rotated, the image will be rotated also
    context.drawImage(img, -imgWidth / 2, -imgHeight / 2);
    // we’re done with the rotating so restore the unrotated context
    context.restore();
    return canvas.toDataURL('image/png');
  }

  private newCanvasSize(w: number, h: number, a: number): ISizeObject {
    const rads = a * Math.PI / 180;
    let c = Math.cos(rads);
    let s = Math.sin(rads);
    if (s < 0) s = -s;
    if (c < 0) c = -c;

    const size: ISizeObject = { width: 0, height: 0};
    size.width = h * s + w * c;
    size.height = h * c + w * s ;

    return size;
  }

  private setMatrixValues(matrix: number[]): void {
    if (!this.imageElm) return;
    this.matrixValues = matrix;
    const imageElm = this.imageElm;
    const transformString = `matrix(${matrix[0]}, ${matrix[1]}, ${matrix[2]}, ${matrix[3]}, ${matrix[4]}, ${matrix[5]})`;

    imageElm.style.transform = transformString;
  }

  private recalculateImageAndContainerRects(): void {
    this.imageDimensions = this.imageElm.getBoundingClientRect();
    this.imageContainerDimensions = this.container.getBoundingClientRect();
  }

  private get imageZoomPercentage(): number {
    const imageElm = this.imageElm;
    if (!imageElm) return NaN;
    return Math.floor((imageElm.clientWidth * this.currentScale) / imageElm.naturalWidth * 100);
  }

  private get scaleAtMaxZoom(): number {
    const imageElm = this.imageElm;
    if (!imageElm) return NaN;
    return this.maxZoomValue * imageElm.naturalWidth / imageElm.clientWidth / 100;
  }
}
